export interface ExecutivePractice {
  id: string;
  title: string;
  badge: string;
  tagline: string;
  description: string;
  typicalRoles: string[];
  baseFeePercentage: number; // e.g. 25% or 20%
  guaranteeDays: number; // e.g. 90 days
  averagePlacementDays: number; // e.g. 21 days
  rolesBenchmarkInr: { min: number; max: number; default: number }; // CTC in INR (Lakhs)
  rolesBenchmarkUsd: { min: number; max: number; default: number }; // CTC in USD
}

export const EXECUTIVE_PRACTICES: ExecutivePractice[] = [
  {
    id: "c-suite",
    title: "C-Suite & Executive Leadership",
    badge: "Retained Search",
    tagline: "Boardroom & CXO appointments with confidential headhunting",
    description:
      "Exclusive executive searches for Chief Executive, Technology, Financial, and Operating Officers. Deep market mapping, candidate discretion, and holistic leadership assessment.",
    typicalRoles: ["Chief Executive Officer (CEO)", "Chief Technology Officer (CTO)", "Chief Financial Officer (CFO)", "Chief Product Officer (CPO)"],
    baseFeePercentage: 25,
    guaranteeDays: 180,
    averagePlacementDays: 28,
    rolesBenchmarkInr: { min: 6000000, max: 25000000, default: 9000000 }, // ₹60L - ₹2.5Cr (default ₹90L)
    rolesBenchmarkUsd: { min: 220000, max: 550000, default: 320000 },
  },
  {
    id: "tech-ai",
    title: "Tech & AI Engineering Talent",
    badge: "High Acuity",
    tagline: "Staff+ engineers, ML research scientists & engineering leaders",
    description:
      "Vetted sourcing for specialized engineering roles. Rigorous technical interviews, architecture portfolio evaluation, and compensation benchmarking.",
    typicalRoles: ["Staff / Principal ML Scientist", "VP of Engineering", "Principal Distributed Systems Architect", "Head of AI Infrastructure"],
    baseFeePercentage: 20,
    guaranteeDays: 90,
    averagePlacementDays: 18,
    rolesBenchmarkInr: { min: 4000000, max: 15000000, default: 6500000 }, // ₹40L - ₹1.5Cr (default ₹65L)
    rolesBenchmarkUsd: { min: 160000, max: 380000, default: 240000 },
  },
  {
    id: "gtm-revenue",
    title: "High-Growth Go-To-Market & Sales",
    badge: "Revenue Impact",
    tagline: "Quota-crushing CROs, VPs of Sales & Strategic Enterprise AEs",
    description:
      "Proven commercial leaders with verifiable quota achievement records. We assess territory mastery, enterprise sales cycles, and ARR scaling velocity.",
    typicalRoles: ["Chief Revenue Officer (CRO)", "VP of Global Sales", "Enterprise Strategic Account Director", "Head of Strategic Partnerships"],
    baseFeePercentage: 20,
    guaranteeDays: 90,
    averagePlacementDays: 16,
    rolesBenchmarkInr: { min: 3500000, max: 12000000, default: 5500000 }, // ₹35L - ₹1.2Cr (default ₹55L)
    rolesBenchmarkUsd: { min: 140000, max: 320000, default: 210000 },
  },
  {
    id: "contract-interim",
    title: "Contract-to-Hire & Interim Leadership",
    badge: "Agile Scale",
    tagline: "Immediate fractional CXOs and rapid engineering surge pods",
    description:
      "Flexible deployment of senior leaders for turnarounds, M&A integrations, or pre-funding scale sprints with structured conversion-to-permanent options.",
    typicalRoles: ["Interim CTO / Transition Advisor", "Fractional Head of Talent", "Principal Cloud Transformation Consultant"],
    baseFeePercentage: 18,
    guaranteeDays: 60,
    averagePlacementDays: 7,
    rolesBenchmarkInr: { min: 2500000, max: 8000000, default: 4500000 }, // ₹25L - ₹80L (default ₹45L)
    rolesBenchmarkUsd: { min: 100000, max: 220000, default: 160000 },
  },
];

export interface RecruiterLead {
  id: string;
  name: string;
  role: string;
  rating: number;
  placementsYtd: number;
  avgClosureDays: number;
}

export const EXECUTIVE_RECRUITERS: RecruiterLead[] = [
  {
    id: "REC-VR",
    name: "Vikramaditya Rao",
    role: "Managing Partner • Executive Search",
    rating: 4.98,
    placementsYtd: 24,
    avgClosureDays: 22,
  },
  {
    id: "REC-SJ",
    name: "Sarah Jenkins",
    role: "Partner • Tech & AI Practice",
    rating: 4.95,
    placementsYtd: 36,
    avgClosureDays: 16,
  },
  {
    id: "REC-RK",
    name: "Rohan Kapoor",
    role: "Director • GTM & Commercial",
    rating: 4.92,
    placementsYtd: 29,
    avgClosureDays: 18,
  },
  {
    id: "REC-NS",
    name: "Neha Sen",
    role: "Senior Executive Talent Scout",
    rating: 4.90,
    placementsYtd: 41,
    avgClosureDays: 14,
  },
];

export const HIRING_STAGES = [
  { id: "sourced", name: "Candidate Sourced / Ingested", color: "sky" },
  { id: "screening", name: "HR Screening & Assessment", color: "blue" },
  { id: "shortlisted", name: "Shortlisted for Client Review", color: "indigo" },
  { id: "interview", name: "Client Interview Round", color: "purple" },
  { id: "placed", name: "Offer Released & Placed (Won)", color: "emerald" },
  { id: "archived", name: "Archived / Unsuccessful", color: "rose" },
];

export function calculatePlacementFee(
  practiceId: string,
  annualCtc: number,
  currency: "INR" | "USD"
) {
  const practice = EXECUTIVE_PRACTICES.find((p) => p.id === practiceId) || EXECUTIVE_PRACTICES[0];
  const feePct = practice.baseFeePercentage;
  const placementFee = Math.round((annualCtc * feePct) / 100);
  const gstOrTax = Math.round(placementFee * 0.18);
  const totalWithTax = placementFee + gstOrTax;

  return {
    practiceTitle: practice.title,
    annualCtc,
    feePercentage: feePct,
    placementFee,
    taxAmount: gstOrTax,
    totalInvoiceAmount: totalWithTax,
    guaranteeDays: practice.guaranteeDays,
    expectedClosureDays: practice.averagePlacementDays,
    currency,
  };
}
